import { format, formatDistanceToNow } from 'date-fns';

/** Format a number as Kenyan shillings, no decimals by default. */
export function formatKes(n: number | null | undefined, decimals = 0): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-KE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

/** "12 May 2026" */
export function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  return format(typeof iso === 'string' ? new Date(iso) : iso, 'd MMM yyyy');
}

/** "12 May 2026, 09:42" */
export function formatDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  return format(typeof iso === 'string' ? new Date(iso) : iso, 'd MMM yyyy, HH:mm');
}

/** "2 hours ago" */
export function formatRelative(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  return formatDistanceToNow(typeof iso === 'string' ? new Date(iso) : iso, {
    addSuffix: true,
  });
}

/** Masks an account number to show only the prefix. */
export function maskAccount(n: string | null | undefined): string {
  if (!n) return '—';
  if (n.length <= 6) return n;
  return n.slice(0, n.length - 5) + 'x'.repeat(5);
}

/**
 * Mask a phone number — keeps the country code and the last 3 digits,
 * replaces the middle with bullets. Format-agnostic: works for
 * "+254712345678", "0712 345 678", "+254 712 345 678" etc.
 *
 *   +254712345678 → +254 712 ••• 678
 *   0712345678   → 0712 ••• 678
 */
export function maskPhone(raw: string | null | undefined): string {
  if (!raw) return '—';
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 7) return raw; // too short to meaningfully mask
  const tail = digits.slice(-3);
  // Detect a "+254" country code prefix to keep it visible.
  if (raw.trim().startsWith('+')) {
    const cc = digits.slice(0, digits.length > 10 ? 3 : 0);
    const op = digits.slice(cc.length, cc.length + 3);
    return `+${cc} ${op} ••• ${tail}`.trim();
  }
  // Local 07xx form — keep first 4 digits.
  const head = digits.slice(0, 4);
  return `${head} ••• ${tail}`;
}

/**
 * Mask an email — keeps the first character of the local part and the
 * full domain.
 *
 *   john.smith@example.com → j••••••••••@example.com
 *   ab@x.io               → a••@x.io
 */
export function maskEmail(raw: string | null | undefined): string {
  if (!raw) return '—';
  const at = raw.indexOf('@');
  if (at <= 0) return raw;
  const local = raw.slice(0, at);
  const domain = raw.slice(at);
  if (local.length <= 1) return `${local}••${domain}`;
  return `${local[0]}${'•'.repeat(Math.max(local.length - 1, 2))}${domain}`;
}

/** Initials for an avatar placeholder. */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
